import type {AppState} from '../../../common/store'
import type {SingleSceneState} from '../../../common/components/store/scenesSlice'
import type {AppConfig, DeviceConfig, SceneFullConfig} from '../../../common/components/types'

// Static ground attributes — not editable in the UI, written verbatim into
// every serialized scene. Frontend doesn't track these in Redux.
const GROUND_DEFAULTS = {size: 400, color: '#e8e4e0', roughness: 0.9}

/** Build a merged scene config from a single scene state */
export const serializeScene = (s: SingleSceneState, baseDevice: DeviceConfig): SceneFullConfig => ({
  device: {
    name: s.activeDeviceName,
    os: baseDevice.os,
    body: {
      ...baseDevice.body,
      width: s.deviceWidth,
      height: s.deviceHeight,
      depth: s.deviceDepth,
      cornerRadius: s.deviceCornerRadius,
      edgeBevel: s.deviceEdgeBevel,
    },
    frontFace: {
      ...baseDevice.frontFace,
      inset: s.frontFaceInset,
    },
    display: {
      ...baseDevice.display,
      cornerRadius: s.displayCornerRadius,
      insetX: s.displayInsetX,
      insetY: s.displayInsetY,
    },
    frontFaceFeatures: s.frontFaceFeatures,
    sideEdgeFeatures: s.sideEdgeFeatures,
    materials: {
      ...baseDevice.materials,
      body: {
        ...baseDevice.materials.body,
        color: s.bodyColor,
        metalness: s.bodyMetalness,
        roughness: s.bodyRoughness,
        clearcoat: s.bodyClearcoat,
        clearcoatRoughness: s.bodyClearcoatRoughness,
      },
      frontFace: {
        ...baseDevice.materials.frontFace,
        color: s.frontFaceColor,
        metalness: s.frontFaceMetalness,
        roughness: s.frontFaceRoughness,
        clearcoat: s.frontFaceClearcoat,
        clearcoatRoughness: s.frontFaceClearcoatRoughness,
        reflectivity: s.frontFaceReflectivity,
      },
      buttons: {
        color: s.buttonsColor,
        metalness: s.buttonsMetalness,
        roughness: s.buttonsRoughness,
        clearcoat: s.buttonsClearcoat,
        clearcoatRoughness: s.buttonsClearcoatRoughness,
        reflectivity: s.buttonsReflectivity,
      },
      antenna: {
        ...baseDevice.materials.antenna,
        color: s.antennasColor,
        metalness: s.antennasMetalness,
        roughness: s.antennasRoughness,
        clearcoat: s.antennasClearcoat,
        clearcoatRoughness: s.antennasClearcoatRoughness,
      },
      screw: {
        ...baseDevice.materials.screw,
        color: s.screwsColor,
        metalness: s.screwsMetalness,
        roughness: s.screwsRoughness,
        clearcoat: s.screwsClearcoat,
        clearcoatRoughness: s.screwsClearcoatRoughness,
        reflectivity: s.screwsReflectivity,
      },
      frontSpeaker: {
        ...baseDevice.materials.frontSpeaker,
        color: s.frontSpeakerColor,
        metalness: s.frontSpeakerMetalness,
        roughness: s.frontSpeakerRoughness,
        clearcoat: s.frontSpeakerClearcoat,
        clearcoatRoughness: s.frontSpeakerClearcoatRoughness,
        reflectivity: s.frontSpeakerReflectivity,
      },
      speakerGrill: {
        ...baseDevice.materials.speakerGrill,
        color: s.speakerGrillColor,
        metalness: s.speakerGrillMetalness,
        roughness: s.speakerGrillRoughness,
        clearcoat: s.speakerGrillClearcoat,
        clearcoatRoughness: s.speakerGrillClearcoatRoughness,
        reflectivity: s.speakerGrillReflectivity,
      },
      chargingConnectorTongue: {
        color: s.chargingConnectorTongueColor,
        metalness: s.chargingConnectorTongueMetalness,
        roughness: s.chargingConnectorTongueRoughness,
        clearcoat: s.chargingConnectorTongueClearcoat,
        clearcoatRoughness: s.chargingConnectorTongueClearcoatRoughness,
        reflectivity: s.chargingConnectorTongueReflectivity,
      },
      chargingConnectorRim: {
        color: s.chargingConnectorRimColor,
        metalness: s.chargingConnectorRimMetalness,
        roughness: s.chargingConnectorRimRoughness,
        clearcoat: s.chargingConnectorRimClearcoat,
        clearcoatRoughness: s.chargingConnectorRimClearcoatRoughness,
        reflectivity: s.chargingConnectorRimReflectivity,
      },
    },
    surfaceTexture: {
      noiseIntensity: s.bodyNoiseIntensity,
      normalStrength: s.bodyNormalStrength,
      normalPattern: s.bodyNormalPattern,
    },
    buttonsSurfaceTexture: {
      noiseIntensity: s.buttonsNoiseIntensity,
      normalStrength: s.buttonsNormalStrength,
      normalPattern: s.buttonsNormalPattern,
    },
  },
  text2d: s.texts,
  text3d: s.texts3d,
  sprites2d: s.sprites2d,
  sprites3d: s.sprites3d,
  customDecals: s.customDecals,
  export: {
    width: s.exportWidth,
    height: s.exportHeight,
    carousel: s.exportCarouselEnabled,
    carouselCount: s.exportCarouselCount,
    carouselGap: s.exportCarouselGap,
  },
  deviceRotationZ: s.deviceRotationZ,
  devicePositionZ: s.devicePositionZ,
  displayReflection: s.displayReflection,
  background: {
    mode: s.bgMode,
    gradientStops: s.bgGradientStops,
    angle: s.bgAngle,
    ...(s.bgImageKey && {
      imageKey: s.bgImageKey,
      imageFit: s.bgImageFit,
      imageOffsetX: s.bgImageOffsetX,
      imageOffsetY: s.bgImageOffsetY,
      imageZoom: s.bgImageZoom,
    }),
  },
  camera: {
    position: s.cameraPosition,
    rotation: s.cameraRotation,
  },
  lighting: {
    ambient: {
      intensity: s.ambientIntensity,
      color: s.ambientColor,
    },
    lights: s.lights,
  },
  environment: {
    preset: s.envPreset,
    envMapIntensity: s.envMapIntensity,
  },
  ground: {
    ...GROUND_DEFAULTS,
    offset: s.groundDistance,
  },
  ...(s.screenImageKey && {textureKey: s.screenImageKey}),
  textureUrl: s.screenImageKey ? '' : s.screenImageUrl,
})

/** Build the full config with all scenes */
export const serializeConfig = (state: AppState, baseDevice: DeviceConfig): AppConfig => ({
  scenes: state.scenes.scenes.map((s) => serializeScene(s, baseDevice)),
})
